import { useState } from "react";
import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import Reveal from "@/components/Reveal";
import { getAnalyticsConsent, setAnalyticsConsent } from "@/lib/analytics";

// PUBLISH-DAY TASK: set this to the actual go-live date when the site is published.
const PUBLICATION_DATE = "October 2026";

/** Lets visitors review or change their analytics consent at any time. */
function AnalyticsChoice() {
    const [choice, setChoice] = useState(() => getAnalyticsConsent());
    const label =
        choice === "granted"
            ? "Analytics: On"
            : choice === "denied"
              ? "Analytics: Off"
              : "Analytics: No choice made yet";
    const update = (value) => {
        setAnalyticsConsent(value);
        setChoice(value);
    };
    return (
        <div
            data-testid="privacy-analytics-choice"
            className="mt-6 flex flex-wrap items-center gap-4 border p-5"
            style={{ borderColor: "var(--hh-border)", background: "var(--hh-bg-2)" }}
        >
            <span
                data-testid="privacy-analytics-status"
                className="text-xs tracking-[0.28em] uppercase"
                style={{ color: "var(--hh-gold)" }}
            >
                {label}
            </span>
            <div className="flex gap-3 md:ml-auto">
                <button
                    data-testid="privacy-analytics-on"
                    type="button"
                    className="btn-ghost"
                    onClick={() => update("granted")}
                >
                    Turn On
                </button>
                <button
                    data-testid="privacy-analytics-off"
                    type="button"
                    className="btn-ghost"
                    onClick={() => update("denied")}
                >
                    Turn Off
                </button>
            </div>
        </div>
    );
}

/**
 * Privacy policy — strictly factual to what the site actually does.
 * No invented licensing/insurance/credential claims.
 */
export default function Privacy() {
    return (
        <div data-testid="privacy-page">
            <SEOHead
                title="Privacy Policy | Harris Homes LLC"
                description="How Harris Homes LLC collects, uses, and protects the information submitted through harrishomesok.com."
                canonical="https://harrishomesok.com/privacy"
            />
            <section className="pt-20 md:pt-32 pb-20 px-6 md:px-10">
                <div className="max-w-[900px] mx-auto">
                    <Reveal>
                        <div className="overline mb-6">Legal</div>
                        <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
                            Privacy Policy
                        </h1>
                        <p
                            className="mt-6 text-sm"
                            style={{ color: "var(--hh-text-dim)" }}
                        >
                            Last updated: {PUBLICATION_DATE}
                        </p>
                    </Reveal>

                    <div
                        className="prose-content mt-14 space-y-10 text-base md:text-lg leading-relaxed"
                        style={{ color: "var(--hh-text-dim)" }}
                    >
                        <section>
                            <h2 className="font-display text-2xl md:text-3xl mb-4" style={{ color: "var(--hh-text)" }}>
                                Who we are
                            </h2>
                            <p>
                                Harris Homes LLC is a residential builder based in Tahlequah, Oklahoma.
                                This site, harrishomesok.com, is operated by Harris Homes LLC. If you
                                have questions about this policy, contact us at{" "}
                                <a
                                    href="mailto:4harrishomes@gmail.com"
                                    className="underline underline-offset-4"
                                    style={{ color: "var(--hh-gold)" }}
                                >
                                    4harrishomes@gmail.com
                                </a>
                                {" "}or (918) 373-4341.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl mb-4" style={{ color: "var(--hh-text)" }}>
                                Information we collect
                            </h2>
                            <p>When you submit the contact form on this website, we collect:</p>
                            <ul className="list-disc pl-6 mt-3 space-y-2">
                                <li>Your name</li>
                                <li>Your phone number</li>
                                <li>Your email address</li>
                                <li>The project type you select</li>
                                <li>The message you write us</li>
                            </ul>
                            <p className="mt-4">
                                We do not knowingly collect any other personal information through
                                this website. We do not sell personal information.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl mb-4" style={{ color: "var(--hh-text)" }}>
                                How we use your information
                            </h2>
                            <p>
                                We use the information you submit solely to respond to your inquiry
                                and discuss a potential project. We may store contact submissions in
                                our records so we can follow up and keep a history of
                                conversations.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl mb-4" style={{ color: "var(--hh-text)" }}>
                                Analytics and cookies
                            </h2>
                            <p>
                                This site uses Google Analytics 4 to understand how visitors use the
                                site (pages viewed, buttons clicked, device type) — but only if you
                                accept analytics in the consent notice shown when you first visit.
                                If you decline, Google Analytics never loads and no analytics cookies
                                are set. Google Analytics 4 anonymizes IP addresses by default. You
                                can change your choice at any time below, and you can opt out of
                                Google Analytics across the web using the{" "}
                                <a
                                    href="https://tools.google.com/dlpage/gaoptout"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline underline-offset-4"
                                    style={{ color: "var(--hh-gold)" }}
                                >
                                    Google Analytics Opt-Out Browser Add-On
                                </a>
                                .
                            </p>
                            <AnalyticsChoice />
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl mb-4" style={{ color: "var(--hh-text)" }}>
                                Third-party services
                            </h2>
                            <p>
                                We use a limited number of third-party services to operate this site:
                            </p>
                            <ul className="list-disc pl-6 mt-3 space-y-2">
                                <li><strong>Google Analytics</strong> — site usage analytics</li>
                                <li><strong>Resend</strong> — delivery of inquiry notifications by email</li>
                                <li><strong>MongoDB</strong> — storage of inquiry submissions</li>
                            </ul>
                            <p className="mt-4">
                                Each of these services has its own privacy practices. We only share
                                the data necessary for them to perform their function.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl mb-4" style={{ color: "var(--hh-text)" }}>
                                Data retention
                            </h2>
                            <p>
                                We retain inquiry submissions for as long as needed to communicate
                                with you about a potential or active project. You can request
                                deletion of your information at any time by emailing{" "}
                                <a
                                    href="mailto:4harrishomes@gmail.com"
                                    className="underline underline-offset-4"
                                    style={{ color: "var(--hh-gold)" }}
                                >
                                    4harrishomes@gmail.com
                                </a>
                                .
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl mb-4" style={{ color: "var(--hh-text)" }}>
                                Your rights
                            </h2>
                            <p>
                                Depending on where you live, you may have rights to access, correct,
                                or delete personal information we hold about you, and to object to or
                                restrict how we use it. To exercise any of these rights, email us at
                                the address above.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl mb-4" style={{ color: "var(--hh-text)" }}>
                                Children
                            </h2>
                            <p>
                                This site is not directed to children under 13, and we do not
                                knowingly collect personal information from children.
                            </p>
                        </section>

                        <section>
                            <h2 className="font-display text-2xl md:text-3xl mb-4" style={{ color: "var(--hh-text)" }}>
                                Changes to this policy
                            </h2>
                            <p>
                                We may update this policy as our practices or applicable law change.
                                The “Last updated” date at the top of this page will reflect any
                                changes.
                            </p>
                        </section>

                        <div className="pt-6">
                            <Link to="/contact" className="btn-gold">Contact Us</Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
